import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'

import { Row } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { isAxiosError } from '@/api/business' 
import { ImpedimentTable } from '../..'
import { updateImpediment } from '@/api/business/impediment'
import { impedimentKeys } from '@/queries/useImpedimentQuery'
import Button from '@/components/Button'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { useEffect } from 'react'  
import { Input } from '@/components/ui/input'

interface EditDialogProps {
    row: Row<ImpedimentTable>
    isOpen: boolean
    onClose: () => void
}

const EditDialog = ({ row, isOpen, onClose }: EditDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast() 

    const schema = z.object({  
        DS_IMPEDITIVO: z.string() 
    })
 
    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: { 
            DS_IMPEDITIVO: row.original.DS_IMPEDITIVO || "", 
        },
    })

    useEffect(() => { 
        form.reset({ 
            DS_IMPEDITIVO: row.original.DS_IMPEDITIVO || "", 
        })
    }, [row.original])

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: updateImpediment,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: impedimentKeys.lists() }) 
            toast({
                title: 'Operação atualizado com sucesso',
            })
            onClose()
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível editar a Operação. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao editar a Operação',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    }) 
    
    const onSubmit = (data: z.infer<typeof schema>) => {
        mutate({
            SK_IMPEDITIVO: row.original.SK_IMPEDITIVO,
           ...data
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[400px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Editar Operação</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >  
                        <FormField
                            name="DS_IMPEDITIVO"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Impeditivo</FormLabel>
                                    <FormControl>
                                        <Input {...field} className='w-full bg-gray-600 text-sm rounded-md px-4 py-2 text-gray-50 outline-none focus:border-primary-500 border-none' /> 
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />   
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default EditDialog
